import React, { useState, useEffect } from "react";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { FileUpload } from "primereact/fileupload";
import { Card } from "primereact/card";
import { Divider } from "primereact/divider";
import { toastify } from "../../../../utils/toast";
import { makeRequest2 } from "../../../../utils/makeRequest";

const billingSettingsURL = "/api/core/billing/settings";

// SVG placeholder for logo
const LogoPlaceholder = () => (
  <div
    className="text-center p-4 bg-gradient"
    style={{
      background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      borderRadius: "12px",
    }}
  >
    <svg
      width="180"
      height="90"
      viewBox="0 0 200 100"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className="rounded"
      style={{
        backgroundColor: "rgba(255,255,255,0.1)",
        backdropFilter: "blur(10px)",
      }}
    >
      <rect width="200" height="100" fill="rgba(255,255,255,0.1)" rx="8" />
      <circle cx="100" cy="40" r="18" fill="rgba(255,255,255,0.8)" />
      <path d="M100 20 L115 35 L100 50 L85 35 Z" fill="rgba(255,255,255,0.6)" />
      <text
        x="100"
        y="75"
        textAnchor="middle"
        fontSize="11"
        fill="rgba(255,255,255,0.9)"
        fontFamily="Arial, sans-serif"
        fontWeight="600"
      >
        Company Logo
      </text>
    </svg>
  </div>
);

function BillingSettingsTab() {
  const [headerSettings, setHeaderSettings] = useState({
    companyName: "",
    companyAddress: "",
    companyPhone: "",
    companyEmail: "",
    companyWebsite: "",
    taxNumber: "",
    logo: null,
    logoPreview: null,
  });

  const [footerSettings, setFooterSettings] = useState({
    bankName: "",
    accountName: "",
    accountNumber: "",
    swiftCode: "",
    paymentTerms: "Net 30 days",
    latePaymentFee: "",
    taxRate: "",
    additionalNotes: "",
    supportEmail: "",
    supportPhone: "",
    termsAndConditions: "",
  });

  const [loading, setLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [hasSettings, setHasSettings] = useState(false);

  // Load settings from API
  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await makeRequest2(billingSettingsURL, "GET", {});
      console.log("API Response:", response); // Debugging
      if (response.success && response.data) {
        // Handle nested data structure - the actual data is at response.data.data
        const actualData = response.data.data || response.data;
        const headerData = actualData.header || {};
        const footerData = actualData.footer || {};

        console.log("Header data:", headerData); // Additional debugging
        console.log("Footer data:", footerData); // Additional debugging

        setHeaderSettings({
          companyName: headerData.companyName || "",
          companyAddress: headerData.companyAddress || "",
          companyPhone: headerData.companyPhone || "",
          companyEmail: headerData.companyEmail || "",
          companyWebsite: headerData.companyWebsite || "",
          taxNumber: headerData.taxNumber || "",
          logo: null, // Reset logo file, use logoPreview for display
          logoPreview: headerData.logo || null,
        });
        setFooterSettings({
          bankName: footerData.bankName || "",
          accountName: footerData.accountName || "",
          accountNumber: footerData.accountNumber || "",
          swiftCode: footerData.swiftCode || "",
          paymentTerms: footerData.paymentTerms || "Net 30 days",
          latePaymentFee: footerData.latePaymentFee || "",
          taxRate: footerData.taxRate || "",
          additionalNotes: footerData.additionalNotes || "",
          supportEmail: footerData.supportEmail || "",
          supportPhone: footerData.supportPhone || "",
          termsAndConditions: footerData.termsAndConditions || "",
        });
        setHasSettings(true);
        setIsEditMode(false);
      } else {
        throw new Error(response.error || "Failed to load settings");
      }
    } catch (error) {
      console.error("Error loading settings:", error);
      if (error.message.includes("404")) {
        setHasSettings(false);
        setIsEditMode(true);
        toastify(
          "No billing settings found, please create new settings",
          "info",
        );
      } else {
        toastify("Error loading settings: " + error.message, "error");
      }
    }
  };

  const handleHeaderChange = (field, value) => {
    setHeaderSettings((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleFooterChange = (field, value) => {
    setFooterSettings((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleLogoUpload = (event) => {
    const file = event.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        setHeaderSettings((prev) => ({
          ...prev,
          logo: file,
          logoPreview: e.target.result,
        }));
      };
      reader.readAsDataURL(file);
    }
  };

  const saveSettings = async () => {
    setLoading(true);
    try {
      const settingsData = {
        header: {
          ...headerSettings,
          logo: headerSettings.logoPreview, // Send base64 string
        },
        footer: footerSettings,
      };

      const response = await makeRequest2(
        billingSettingsURL,
        "PUT",
        settingsData,
      );
      if (response.success) {
        toastify("Billing settings saved successfully!", "success");
        setHasSettings(true);
        setIsEditMode(false);
      } else {
        throw new Error(response.error || "Failed to save settings");
      }
    } catch (error) {
      toastify("Error saving settings: " + error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const resetSettings = async () => {
    try {
      const response = await makeRequest2(billingSettingsURL, "DELETE", {});
      if (response.success) {
        setHeaderSettings({
          companyName: "",
          companyAddress: "",
          companyPhone: "",
          companyEmail: "",
          companyWebsite: "",
          taxNumber: "",
          logo: null,
          logoPreview: null,
        });
        setFooterSettings({
          bankName: "",
          accountName: "",
          accountNumber: "",
          swiftCode: "",
          paymentTerms: "Net 30 days",
          latePaymentFee: "",
          taxRate: "",
          additionalNotes: "",
          supportEmail: "",
          supportPhone: "",
          termsAndConditions: "",
        });
        setHasSettings(false);
        setIsEditMode(true);
        toastify("Settings deleted successfully!", "info");
      } else {
        throw new Error(response.error || "Failed to reset settings");
      }
    } catch (error) {
      toastify("Error resetting settings: " + error.message, "error");
    }
  };

  const handleEditClick = () => {
    setIsEditMode(true);
  };

  // Render read-only card for viewing settings
  const renderViewCard = () => (
    <div className="row">
      {/* Header Information Card */}
      <div className="col-lg-6 mb-4">
        <Card
          title={
            <div className="d-flex align-items-center gap-2">
              <i className="ti ti-building text-primary fs-5"></i>
              <span className="text-primary fw-bold">Company Information</span>
            </div>
          }
          className="h-100 shadow-sm border-0"
          style={{ borderLeft: "4px solid #0d6efd" }}
        >
          <div className="mb-4 text-center">
            {headerSettings.logoPreview ? (
              <img
                src={headerSettings.logoPreview}
                alt="Company Logo"
                style={{ maxHeight: "120px", maxWidth: "250px" }}
                className="border rounded shadow-sm"
              />
            ) : (
              <div style={{ display: "inline-block" }}>
                <LogoPlaceholder />
              </div>
            )}
          </div>

          <div className="row g-3">
            <div className="col-12">
              <div className="p-3 bg-light rounded-3">
                <div className="d-flex align-items-center mb-2">
                  <i className="ti ti-building-store text-primary me-2"></i>
                  <small className="text-muted fw-medium">COMPANY NAME</small>
                </div>
                <div className="fw-semibold text-dark">
                  {headerSettings.companyName || "Not specified"}
                </div>
              </div>
            </div>

            <div className="col-12">
              <div className="p-3 bg-light rounded-3">
                <div className="d-flex align-items-center mb-2">
                  <i className="ti ti-map-pin text-primary me-2"></i>
                  <small className="text-muted fw-medium">ADDRESS</small>
                </div>
                <div className="fw-semibold text-dark">
                  {headerSettings.companyAddress || "Not specified"}
                </div>
              </div>
            </div>

            <div className="col-md-6">
              <div className="p-3 bg-light rounded-3">
                <div className="d-flex align-items-center mb-2">
                  <i className="ti ti-phone text-primary me-2"></i>
                  <small className="text-muted fw-medium">PHONE</small>
                </div>
                <div className="fw-semibold text-dark">
                  {headerSettings.companyPhone || "Not specified"}
                </div>
              </div>
            </div>

            <div className="col-md-6">
              <div className="p-3 bg-light rounded-3">
                <div className="d-flex align-items-center mb-2">
                  <i className="ti ti-mail text-primary me-2"></i>
                  <small className="text-muted fw-medium">EMAIL</small>
                </div>
                <div className="fw-semibold text-dark text-break">
                  {headerSettings.companyEmail || "Not specified"}
                </div>
              </div>
            </div>

            <div className="col-md-6">
              <div className="p-3 bg-light rounded-3">
                <div className="d-flex align-items-center mb-2">
                  <i className="ti ti-world text-primary me-2"></i>
                  <small className="text-muted fw-medium">WEBSITE</small>
                </div>
                <div className="fw-semibold text-dark text-break">
                  {headerSettings.companyWebsite || "Not specified"}
                </div>
              </div>
            </div>

            <div className="col-md-6">
              <div className="p-3 bg-light rounded-3">
                <div className="d-flex align-items-center mb-2">
                  <i className="ti ti-id text-primary me-2"></i>
                  <small className="text-muted fw-medium">TAX NUMBER</small>
                </div>
                <div className="fw-semibold text-dark">
                  {headerSettings.taxNumber || "Not specified"}
                </div>
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Footer Information Card */}
      <div className="col-lg-6 mb-4">
        <Card
          title={
            <div className="d-flex align-items-center gap-2">
              <i className="ti ti-receipt text-success fs-5"></i>
              <span className="text-success fw-bold">
                Billing & Payment Information
              </span>
            </div>
          }
          className="h-100 shadow-sm border-0"
          style={{ borderLeft: "4px solid #198754" }}
        >
          <div className="row g-3">
            {/* Payment Information Section */}
            <div className="col-12">
              <h6 className="text-success mb-3 d-flex align-items-center">
                <i className="ti ti-credit-card me-2"></i>
                Payment Details
              </h6>
            </div>

            <div className="col-md-6">
              <div className="p-3 bg-light rounded-3">
                <div className="d-flex align-items-center mb-2">
                  <i className="ti ti-building-bank text-success me-2"></i>
                  <small className="text-muted fw-medium">BANK NAME</small>
                </div>
                <div className="fw-semibold text-dark">
                  {footerSettings.bankName || "Not specified"}
                </div>
              </div>
            </div>

            <div className="col-md-6">
              <div className="p-3 bg-light rounded-3">
                <div className="d-flex align-items-center mb-2">
                  <i className="ti ti-user text-success me-2"></i>
                  <small className="text-muted fw-medium">ACCOUNT NAME</small>
                </div>
                <div className="fw-semibold text-dark">
                  {footerSettings.accountName || "Not specified"}
                </div>
              </div>
            </div>

            <div className="col-md-6">
              <div className="p-3 bg-light rounded-3">
                <div className="d-flex align-items-center mb-2">
                  <i className="ti ti-hash text-success me-2"></i>
                  <small className="text-muted fw-medium">ACCOUNT NUMBER</small>
                </div>
                <div className="fw-semibold text-dark">
                  {footerSettings.accountNumber || "Not specified"}
                </div>
              </div>
            </div>

            <div className="col-md-6">
              <div className="p-3 bg-light rounded-3">
                <div className="d-flex align-items-center mb-2">
                  <i className="ti ti-code text-success me-2"></i>
                  <small className="text-muted fw-medium">SWIFT CODE</small>
                </div>
                <div className="fw-semibold text-dark">
                  {footerSettings.swiftCode || "Not specified"}
                </div>
              </div>
            </div>

            {/* Terms & Conditions Section */}
            <div className="col-12 mt-4">
              <h6 className="text-success mb-3 d-flex align-items-center">
                <i className="ti ti-file-text me-2"></i>
                Terms & Support
              </h6>
            </div>

            <div className="col-md-4">
              <div className="p-3 bg-light rounded-3">
                <div className="d-flex align-items-center mb-2">
                  <i className="ti ti-calendar text-success me-2"></i>
                  <small className="text-muted fw-medium">PAYMENT TERMS</small>
                </div>
                <div className="fw-semibold text-dark">
                  {footerSettings.paymentTerms || "Not specified"}
                </div>
              </div>
            </div>

            <div className="col-md-4">
              <div className="p-3 bg-light rounded-3">
                <div className="d-flex align-items-center mb-2">
                  <i className="ti ti-percentage text-success me-2"></i>
                  <small className="text-muted fw-medium">LATE FEE (%)</small>
                </div>
                <div className="fw-semibold text-dark">
                  {footerSettings.latePaymentFee
                    ? `${footerSettings.latePaymentFee}%`
                    : "Not specified"}
                </div>
              </div>
            </div>

            <div className="col-md-4">
              <div className="p-3 bg-light rounded-3">
                <div className="d-flex align-items-center mb-2">
                  <i className="ti ti-receipt-tax text-success me-2"></i>
                  <small className="text-muted fw-medium">TAX RATE (%)</small>
                </div>
                <div className="fw-semibold text-dark">
                  {footerSettings.taxRate
                    ? `${footerSettings.taxRate}%`
                    : "Not specified"}
                </div>
              </div>
            </div>

            <div className="col-md-6">
              <div className="p-3 bg-light rounded-3">
                <div className="d-flex align-items-center mb-2">
                  <i className="ti ti-mail text-success me-2"></i>
                  <small className="text-muted fw-medium">SUPPORT EMAIL</small>
                </div>
                <div className="fw-semibold text-dark text-break">
                  {footerSettings.supportEmail || "Not specified"}
                </div>
              </div>
            </div>

            <div className="col-md-6">
              <div className="p-3 bg-light rounded-3">
                <div className="d-flex align-items-center mb-2">
                  <i className="ti ti-phone text-success me-2"></i>
                  <small className="text-muted fw-medium">SUPPORT PHONE</small>
                </div>
                <div className="fw-semibold text-dark">
                  {footerSettings.supportPhone || "Not specified"}
                </div>
              </div>
            </div>

            {(footerSettings.additionalNotes ||
              footerSettings.termsAndConditions) && (
              <>
                <div className="col-12">
                  <div className="p-3 bg-light rounded-3">
                    <div className="d-flex align-items-center mb-2">
                      <i className="ti ti-notes text-success me-2"></i>
                      <small className="text-muted fw-medium">
                        ADDITIONAL NOTES
                      </small>
                    </div>
                    <div className="fw-semibold text-dark">
                      {footerSettings.additionalNotes || "Not specified"}
                    </div>
                  </div>
                </div>

                <div className="col-12">
                  <div className="p-3 bg-light rounded-3">
                    <div className="d-flex align-items-center mb-2">
                      <i className="ti ti-file-description text-success me-2"></i>
                      <small className="text-muted fw-medium">
                        TERMS & CONDITIONS
                      </small>
                    </div>
                    <div className="fw-semibold text-dark">
                      {footerSettings.termsAndConditions || "Not specified"}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </Card>
      </div>

      {/* Action Buttons */}
      <div className="col-12">
        <div className="d-flex gap-3 justify-content-end p-3 bg-light rounded-3">
          <Button
            label="Edit Settings"
            icon="ti ti-edit"
            className="btn btn-primary px-4 py-2"
            onClick={handleEditClick}
          />
          <Button
            label="Delete Settings"
            icon="ti ti-trash"
            className="btn btn-outline-danger px-4 py-2"
            onClick={resetSettings}
          />
        </div>
      </div>
    </div>
  );

  // Render input form for editing settings
  const renderEditForm = () => (
    <div className="row">
      {/* Header Settings */}
      <div className="col-md-6">
        <Card title="Invoice Header Settings" className="mb-4">
          <div className="row">
            <div className="col-12 mb-3">
              <label className="form-label">Company Logo</label>
              <FileUpload
                mode="basic"
                name="logo"
                accept="image/*"
                maxFileSize={1000000}
                onSelect={handleLogoUpload}
                auto
                chooseLabel="Upload Logo"
                className="d-block"
              />
              <div className="mt-2">
                {headerSettings.logoPreview ? (
                  <img
                    src={headerSettings.logoPreview}
                    alt="Logo Preview"
                    style={{ maxHeight: "100px", maxWidth: "200px" }}
                    className="border rounded"
                  />
                ) : (
                  <LogoPlaceholder />
                )}
              </div>
            </div>

            <div className="col-12 mb-3">
              <label className="form-label">Company Name *</label>
              <InputText
                value={headerSettings.companyName}
                onChange={(e) =>
                  handleHeaderChange("companyName", e.target.value)
                }
                placeholder="Enter company name"
                className="w-100"
              />
            </div>

            <div className="col-12 mb-3">
              <label className="form-label">Company Address *</label>
              <InputTextarea
                value={headerSettings.companyAddress}
                onChange={(e) =>
                  handleHeaderChange("companyAddress", e.target.value)
                }
                placeholder="Enter company address"
                rows={3}
                className="w-100"
              />
            </div>

            <div className="col-12 mb-3">
              <label className="form-label">Phone Number</label>
              <InputText
                value={headerSettings.companyPhone}
                onChange={(e) =>
                  handleHeaderChange("companyPhone", e.target.value)
                }
                placeholder="Enter phone number"
                className="w-100"
              />
            </div>

            <div className="col-12 mb-3">
              <label className="form-label">Email Address</label>
              <InputText
                value={headerSettings.companyEmail}
                onChange={(e) =>
                  handleHeaderChange("companyEmail", e.target.value)
                }
                placeholder="Enter email address"
                className="w-100"
              />
            </div>

            <div className="col-12 mb-3">
              <label className="form-label">Website</label>
              <InputText
                value={headerSettings.companyWebsite}
                onChange={(e) =>
                  handleHeaderChange("companyWebsite", e.target.value)
                }
                placeholder="Enter website URL"
                className="w-100"
              />
            </div>

            <div className="col-12 mb-3">
              <label className="form-label">Tax/Registration Number</label>
              <InputText
                value={headerSettings.taxNumber}
                onChange={(e) =>
                  handleHeaderChange("taxNumber", e.target.value)
                }
                placeholder="Enter tax/registration number"
                className="w-100"
              />
            </div>
          </div>
        </Card>
      </div>

      {/* Footer Settings */}
      <div className="col-md-6">
        <Card title="Invoice Footer Settings" className="mb-4">
          <div className="row">
            <div className="col-12 mb-3">
              <h6 className="text-primary">Payment Information</h6>
              <Divider />
            </div>

            <div className="col-12 mb-3">
              <label className="form-label">Bank Name</label>
              <InputText
                value={footerSettings.bankName}
                onChange={(e) => handleFooterChange("bankName", e.target.value)}
                placeholder="Enter bank name"
                className="w-100"
              />
            </div>

            <div className="col-12 mb-3">
              <label className="form-label">Account Name</label>
              <InputText
                value={footerSettings.accountName}
                onChange={(e) =>
                  handleFooterChange("accountName", e.target.value)
                }
                placeholder="Enter account name"
                className="w-100"
              />
            </div>

            <div className="col-12 mb-3">
              <label className="form-label">Account Number</label>
              <InputText
                value={footerSettings.accountNumber}
                onChange={(e) =>
                  handleFooterChange("accountNumber", e.target.value)
                }
                placeholder="Enter account number"
                className="w-100"
              />
            </div>

            <div className="col-12 mb-3">
              <label className="form-label">SWIFT Code</label>
              <InputText
                value={footerSettings.swiftCode}
                onChange={(e) =>
                  handleFooterChange("swiftCode", e.target.value)
                }
                placeholder="Enter SWIFT code"
                className="w-100"
              />
            </div>

            <div className="col-12 mb-3">
              <h6 className="text-primary">Invoice Terms</h6>
              <Divider />
            </div>

            <div className="col-12 mb-3">
              <label className="form-label">Payment Terms</label>
              <InputText
                value={footerSettings.paymentTerms}
                onChange={(e) =>
                  handleFooterChange("paymentTerms", e.target.value)
                }
                placeholder="e.g., Net 30 days"
                className="w-100"
              />
            </div>

            <div className="col-12 mb-3">
              <label className="form-label">Late Payment Fee (%)</label>
              <InputText
                value={footerSettings.latePaymentFee}
                onChange={(e) =>
                  handleFooterChange("latePaymentFee", e.target.value)
                }
                placeholder="Enter late payment fee percentage"
                className="w-100"
              />
            </div>

            <div className="col-12 mb-3">
              <label className="form-label">Tax Rate (%)</label>
              <InputText
                value={footerSettings.taxRate}
                onChange={(e) => handleFooterChange("taxRate", e.target.value)}
                placeholder="Enter tax rate percentage"
                className="w-100"
              />
            </div>

            <div className="col-12 mb-3">
              <label className="form-label">Support Email</label>
              <InputText
                value={footerSettings.supportEmail}
                onChange={(e) =>
                  handleFooterChange("supportEmail", e.target.value)
                }
                placeholder="Enter support email"
                className="w-100"
              />
            </div>

            <div className="col-12 mb-3">
              <label className="form-label">Support Phone</label>
              <InputText
                value={footerSettings.supportPhone}
                onChange={(e) =>
                  handleFooterChange("supportPhone", e.target.value)
                }
                placeholder="Enter support phone"
                className="w-100"
              />
            </div>

            <div className="col-12 mb-3">
              <label className="form-label">Additional Notes</label>
              <InputTextarea
                value={footerSettings.additionalNotes}
                onChange={(e) =>
                  handleFooterChange("additionalNotes", e.target.value)
                }
                placeholder="Enter additional notes"
                rows={3}
                className="w-100"
              />
            </div>

            <div className="col-12 mb-3">
              <label className="form-label">Terms and Conditions</label>
              <InputTextarea
                value={footerSettings.termsAndConditions}
                onChange={(e) =>
                  handleFooterChange("termsAndConditions", e.target.value)
                }
                placeholder="Enter terms and conditions"
                rows={4}
                className="w-100"
              />
            </div>
          </div>
        </Card>
      </div>

      {/* Action Buttons */}
      <div className="col-12">
        <div className="d-flex gap-2 justify-content-end">
          <Button
            label="Cancel"
            icon="ti ti-x"
            className="btn btn-outline-secondary"
            onClick={() => setIsEditMode(false)}
            disabled={!hasSettings}
          />
          <Button
            label="Save Settings"
            icon="ti ti-device-floppy"
            className="btn btn-primary"
            onClick={saveSettings}
            loading={loading}
          />
        </div>
      </div>
    </div>
  );

  console.log("Rendering with headerSettings:", headerSettings); // Debugging
  console.log("Rendering with footerSettings:", footerSettings); // Debugging

  return (
    <div className="row">
      {hasSettings && !isEditMode ? renderViewCard() : renderEditForm()}
    </div>
  );
}

export default BillingSettingsTab;
